import { useMemo, useState } from 'react';
import { Box, Chip, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, SearchField } from '../../components/data-grid/RouteDataGrid';
import type { ExportColumn } from '../../utils/export';
import { fmtDateTime } from '../../utils/format';
import type { LoginAuditRow, AuditLogRow } from '../../api/types';
import { useAuditLogList, useLoginAuditList, useMyPermissions } from '../../api/queries';
import { EntityDetailDialog } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { TerminalSessionsPanel } from '../site-controllers/TerminalSessionsPanel';

/**
 * Read-only view over the immutable audit tables.
 *
 *  - **Sign-in events** (`audit.login_audit`) — every login attempt, TOTP
 *    flow step, and password change. Useful for "did Bob log in last week"
 *    and "why is this account locked out".
 *  - **Activity** (`audit.audit_log`) — every operator-scoped state change
 *    (user.create, group.member.added, settings.value.update, etc).
 *
 * Operator users see their operator's rows; ACES sees the platform-wide
 * view. RLS enforces this at the database too.
 */
export default function AuditPage() {
  usePageTitle('Audit log');
  const [tab, setTab] = useState<'login' | 'log' | 'terminal'>('login');
  const perms = useMyPermissions();
  const canViewTerminal = perms.data?.codes?.includes('provisioning.site-controllers.terminal') ?? false;

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Audit log</Typography>
        <Typography variant="body2" color="text.secondary">
          Append-only record of sign-in attempts and operator activity.
          Rows are immutable — corrections happen by adding compensating
          entries, not by editing.
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="login" label="Sign-in events" />
          <Tab value="log"   label="Activity" />
          {canViewTerminal && <Tab value="terminal" label="Terminal sessions" />}
        </Tabs>

        <Box hidden={tab !== 'login'}>
          {tab === 'login' && <SignInTab />}
        </Box>
        <Box hidden={tab !== 'log'}>
          {tab === 'log' && <ActivityTab />}
        </Box>
        {canViewTerminal && (
          <Box hidden={tab !== 'terminal'}>
            {tab === 'terminal' && <TerminalSessionsPanel />}
          </Box>
        )}
      </Stack>
    </>
  );
}

function SignInTab() {
  const list = useLoginAuditList();
  const [selected, setSelected] = useState<LoginAuditRow | null>(null);

  const columns = useMemo<GridColDef<LoginAuditRow>[]>(() => [
    { field: 'occurredAt', headerName: 'When', width: 180,
      renderCell: (p) => (
        <Typography variant="caption">{fmtDateTime(p.row.occurredAt)}</Typography>
      ) },
    { field: 'eventType', headerName: 'Event', width: 200,
      renderCell: (p) => <EventChip event={p.row.eventType} /> },
    { field: 'userDisplayName', headerName: 'User', width: 220,
      renderCell: (p) => (
        <Tooltip title={p.row.userEmail ?? ''}>
          <Typography variant="body2">
            {p.row.userDisplayName ?? p.row.userEmail ?? '—'}
          </Typography>
        </Tooltip>
      ) },
    { field: 'factorUsed', headerName: 'Factor', width: 100,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">{p.row.factorUsed ?? '—'}</Typography>
        </Box>
      ) },
    { field: 'ipAddress', headerName: 'IP', width: 160,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {p.row.ipAddress ?? '—'}
          </Typography>
        </Box>
      ) },
    { field: 'geoipCountry', headerName: 'Country', width: 90,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {p.row.geoipCountry ?? '—'}
          </Typography>
        </Box>
      ) },
    { field: 'userAgent', headerName: 'User agent', flex: 1.2,
      renderCell: (p) => (
        <Tooltip title={p.row.userAgent || ''}>
          <Typography variant="caption" sx={{
            fontFamily: 'monospace', fontSize: 11,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {p.row.userAgent || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    { field: 'outcomeDetail', headerName: 'Detail', flex: 1.2,
      renderCell: (p) => {
        // Successful events have no detail — that's expected, render an
        // em-dash so the empty cell doesn't look like a render failure.
        if (!p.row.outcomeDetail) {
          return <Typography variant="caption" color="text.disabled">—</Typography>;
        }
        return (
          <Tooltip title={p.row.outcomeDetail}>
            <Typography variant="caption" sx={{
              fontFamily: 'monospace', fontSize: 11,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {p.row.outcomeDetail}
            </Typography>
          </Tooltip>
        );
      },
    },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'eventType',  label: 'Event type' },
    { kind: 'text', key: 'userId',     label: 'User ID' },
    { kind: 'text', key: 'ipAddress',  label: 'IP address' },
  ], []);

  const quickFilters = useMemo<QuickFilter<LoginAuditRow>[]>(() => [
    { label: 'All',         filter: () => true },
    { label: 'Failures',    filter: (r) => r.eventType.endsWith('.failure') || r.eventType.endsWith('.failed') },
    { label: 'Successes',   filter: (r) => r.eventType === 'login.success' || r.eventType === 'login.password_ok' || r.eventType === 'totp.setup_confirmed' || r.eventType === 'password.changed' },
    // factorUsed is the authoritative source for "what kind of event was this":
    // password events = factor "password", TOTP events = factor "totp",
    // password.changed has no factor but is plainly password-related.
    { label: 'TOTP',        filter: (r) => r.factorUsed === 'totp' },
    { label: 'Password',    filter: (r) => r.factorUsed === 'password' || r.eventType.startsWith('password.') },
  ], []);

  const exportColumns = useMemo<ExportColumn<LoginAuditRow>[]>(() => [
    { key: 'occurredAt',       label: 'When', format: (v) => fmtDateTime(v) },
    { key: 'eventType',        label: 'Event' },
    { key: 'userDisplayName',  label: 'User' },
    { key: 'userEmail',        label: 'Email' },
    { key: 'userId',           label: 'User ID' },
    { key: 'operatorId',       label: 'Operator ID' },
    { key: 'factorUsed',       label: 'Factor' },
    { key: 'ipAddress',        label: 'IP' },
    { key: 'geoipCountry',     label: 'Country' },
    { key: 'userAgent',        label: 'User agent' },
    { key: 'outcomeDetail',    label: 'Detail' },
    { key: 'correlationId',    label: 'Correlation' },
  ], []);

  const detailFields = useMemo<DetailRow<LoginAuditRow>[]>(() => [
    { label: 'When',           render: (r) => fmtDateTime(r.occurredAt) },
    { label: 'Event',          render: (r) => <EventChip event={r.eventType} /> },
    { label: 'User',           render: (r) => r.userDisplayName ?? r.userEmail ?? '—' },
    { label: 'Email',          render: (r) => r.userEmail ?? '—' },
    { label: 'Factor used',    render: (r) => r.factorUsed ?? '—' },
    { label: 'IP address',     separator: true,
      render: (r) => r.ipAddress
        ? <Typography sx={{ fontFamily: 'monospace' }}>{r.ipAddress}</Typography>
        : '—' },
    { label: 'Country',        render: (r) => r.geoipCountry ?? '—' },
    { label: 'User agent',     render: (r) => r.userAgent
      ? <Typography sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{r.userAgent}</Typography>
      : '—' },
    { label: 'Outcome detail', render: (r) => r.outcomeDetail
      ? <Typography sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{r.outcomeDetail}</Typography>
      : '—' },
    { label: 'Correlation ID', separator: true,
      render: (r) => <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.correlationId}</Typography> },
  ], []);

  return (
    <>
      <RouteDataGrid<LoginAuditRow>
        entityName="Sign-in events"
        queryKey={list.queryKey}
        listFn={list.listFn}
        columns={columns}
        searchFields={searchFields}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — Sign-in events"
        emptyMessage="No sign-in events recorded yet."
        onRowClick={(row) => setSelected(row)}
      />
      <EntityDetailDialog<LoginAuditRow>
        open={selected !== null}
        title={selected ? `Sign-in event · ${selected.eventType}` : 'Sign-in event'}
        data={selected}
        fields={detailFields}
        onClose={() => setSelected(null)}
        maxWidth="md"
      />
    </>
  );
}

function ActivityTab() {
  const list = useAuditLogList();
  const [selected, setSelected] = useState<AuditLogRow | null>(null);

  const columns = useMemo<GridColDef<AuditLogRow>[]>(() => [
    { field: 'occurredAt', headerName: 'When', width: 180,
      renderCell: (p) => (
        <Typography variant="caption">{fmtDateTime(p.row.occurredAt)}</Typography>
      ) },
    { field: 'tier', headerName: 'Tier', width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.row.tier}
              color={p.row.tier === 'platform' ? 'secondary' : 'default'} />
      ) },
    { field: 'eventType', headerName: 'Event', flex: 1.2,
      renderCell: (p) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {p.row.eventType}
        </Typography>
      ),
    },
    { field: 'actorDisplayName', headerName: 'Actor', width: 220,
      renderCell: (p) => (
        <Tooltip title={p.row.actorEmailUnredacted ?? ''}>
          <Typography variant="body2">
            {p.row.actorDisplayName ?? p.row.actorEmailUnredacted ?? p.row.actorType}
          </Typography>
        </Tooltip>
      ) },
    { field: 'subjectResourceType', headerName: 'Subject', flex: 1,
      renderCell: (p) => {
        if (!p.row.subjectResourceType) {
          return <Typography variant="caption" color="text.secondary">—</Typography>;
        }
        // Resolved name + friendly type. If the server couldn't resolve
        // (entity deleted, unknown type) show the type only — never a
        // raw or truncated GUID. House rule: feedback_no_raw_guids_in_ui.
        const friendly = p.row.subjectResourceType.replace(/_/g, ' ');
        const label = p.row.subjectDisplayName
          ? `${p.row.subjectDisplayName} (${friendly})`
          : friendly;
        return <Typography variant="body2">{label}</Typography>;
      } },
    { field: 'outcome', headerName: 'Outcome', width: 110,
      renderCell: (p) => <OutcomeChip outcome={p.row.outcome} /> },
    { field: 'reason', headerName: 'Reason', flex: 1,
      renderCell: (p) => p.row.reason ?? '—' },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'eventType',           label: 'Event type' },
    { kind: 'text', key: 'subjectResourceType', label: 'Subject type' },
    { kind: 'text', key: 'actorEmailUnredacted', label: 'Actor email' },
  ], []);

  const quickFilters = useMemo<QuickFilter<AuditLogRow>[]>(() => [
    { label: 'All',       filter: () => true },
    { label: 'Failures',  filter: (r) => r.outcome !== 'success' },
    { label: 'Users',     filter: (r) => r.eventType.startsWith('user.') },
    { label: 'Settings',  filter: (r) => r.eventType.startsWith('settings.') },
  ], []);

  const exportColumns = useMemo<ExportColumn<AuditLogRow>[]>(() => [
    { key: 'occurredAt',           label: 'When', format: (v) => fmtDateTime(v) },
    { key: 'tier',                 label: 'Tier' },
    { key: 'eventType',            label: 'Event' },
    { key: 'actorEmailUnredacted', label: 'Actor email' },
    { key: 'actorType',            label: 'Actor type' },
    { key: 'subjectResourceType',  label: 'Subject type' },
    { key: 'subjectResourceId',    label: 'Subject ID' },
    { key: 'outcome',              label: 'Outcome' },
    { key: 'reason',               label: 'Reason' },
    { key: 'operatorId',           label: 'Operator' },
    { key: 'correlationId',        label: 'Correlation' },
  ], []);

  const detailFields = useMemo<DetailRow<AuditLogRow>[]>(() => [
    { label: 'When',           render: (r) => fmtDateTime(r.occurredAt) },
    { label: 'Tier',           render: (r) => (
      <Chip size="small" label={r.tier}
            color={r.tier === 'platform' ? 'secondary' : 'default'} />
    ) },
    { label: 'Event type',     render: (r) => (
      <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{r.eventType}</Typography>
    ) },
    { label: 'Outcome',        render: (r) => <OutcomeChip outcome={r.outcome} /> },
    { label: 'Reason',         render: (r) => r.reason ?? '—' },

    { label: 'Actor',          separator: true,
      render: (r) => r.actorDisplayName ?? r.actorEmailUnredacted ?? r.actorType },
    { label: 'Actor email',    render: (r) => r.actorEmailUnredacted ?? '—' },
    { label: 'Actor type',     render: (r) => r.actorType },

    { label: 'Subject',        separator: true,
      render: (r) => {
        if (!r.subjectResourceType) return '—';
        const friendly = r.subjectResourceType.replace(/_/g, ' ');
        return r.subjectDisplayName
          ? `${r.subjectDisplayName} (${friendly})`
          : friendly;
      } },
    { label: 'Subject type',   render: (r) => r.subjectResourceType ?? '—' },

    { label: 'Correlation ID', separator: true,
      render: (r) => (
        <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.correlationId}</Typography>
      ) },
  ], []);

  return (
    <>
      <RouteDataGrid<AuditLogRow>
        entityName="Activity"
        queryKey={list.queryKey}
        listFn={list.listFn}
        columns={columns}
        searchFields={searchFields}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — Activity audit"
        emptyMessage="No activity recorded yet."
        onRowClick={(row) => setSelected(row)}
      />
      <EntityDetailDialog<AuditLogRow>
        open={selected !== null}
        title={selected ? `Activity · ${selected.eventType}` : 'Activity'}
        data={selected}
        fields={detailFields}
        onClose={() => setSelected(null)}
        maxWidth="md"
      />
    </>
  );
}

function EventChip({ event }: { event: string }) {
  const color =
    event === 'login.success' || event === 'login.password_ok' ? 'success' :
    event === 'login.failure' || event === 'totp.failed' || event === 'totp.setup_failed' || event === 'password.change_failed' ? 'error' :
    event.startsWith('totp.') ? 'info' :
    event.startsWith('password.') ? 'warning' : 'default';
  return (
    <Chip
      size="small"
      label={event}
      color={color as 'success' | 'error' | 'info' | 'warning' | 'default'}
      sx={{ fontFamily: 'monospace', fontSize: 11 }}
    />
  );
}

function OutcomeChip({ outcome }: { outcome: string }) {
  const color = outcome === 'success' ? 'success' : outcome === 'failure' ? 'error' : 'default';
  return <Chip size="small" label={outcome} color={color as 'success' | 'error' | 'default'} />;
}
